import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap/dist/js/bootstrap.bundle.min.js";
// Stylesheets the previous Lite layout pulled in. The page no longer uses them, but they are bundled app-wide
// (the roadmap page shares some), so they stay imported in the same order to leave other pages' cascade unchanged.
import "../assets/css/homepage_style.css";
import "../assets/css/reimagine_light.css";
import "../assets/css/lite-theme.css";
import "../assets/css/navigation.css";
import "../assets/css/footer.css";
import "../assets/css/ScrollToExplore.css";
import "../assets/css/recommendation_cards_animation.css";
import "../assets/css/roadmap_path_animation.css";
// Redesigned Lite page (scoped to its own root class; see src/pages/lite and src/pages/landing).
import "./landing/landing.css";
import "./lite/lite.css";
import ScrollToExplore from "../components/ScrollToExplore";
import LandingFooter from "./landing/LandingFooter";
import LandingNav from "./landing/LandingNav";
import { useAppThemeWhileMounted, useLandingFont, useReveal } from "./landing/shared";
import Generator from "./lite/Generator";
import { ConnectModal, LoadingOverlay, UserDetailsModal } from "./lite/Modals";
import { LiteBaseline, LiteCta, LiteFramework, LiteHero, LiteHow, LiteTrends } from "./lite/sections";
import {
  getReimagineLiteReport,
  addReimagineLiteReport,
} from "../services/liteApi";

function ReImagineLite() {
  const navigate = useNavigate();
  const rootRef = useRef<HTMLDivElement>(null);
  const [openCard, setOpenCard] = useState(null);

  // State to track selected filters for each category
  const [selectedFilters] = useState({
    "project-type": [],
    maturity: [],
    sector: [],
    function: [],
    capability: [],
    impact: [],
    dummy1: [],
    dummy2: [],
    dummy3: [],
  });

  // State to track radio button answers for each card
  const [cardAnswers, setCardAnswers] = useState({
    "project-type": "",
    maturity: "",
    sector: "",
    function: "",
    capability: "",
    impact: "",
    dummy1: "",
    dummy2: "",
    dummy3: "",
  });

  // State for user details modal
  const [showModal, setShowModal] = useState(false);
  const [userDetails, setUserDetails] = useState({
    name: "",
    company: "",
    companyUrl: "",
    email: "",
  });

  // State for company autocomplete
  const [companySuggestions, setCompanySuggestions] = useState([]);
  const [showCompanySuggestions, setShowCompanySuggestions] = useState(false);
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);
  const companyInputRef = useRef(null);
  const suggestionsRef = useRef(null);

  // State for "Connect with our Experts" modal
  const [showConnectModal, setShowConnectModal] = useState(false);
  const [connectForm, setConnectForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    company: "",
    phone: "",
    message: "",
  });

  // State to track which cards need validation indicators
  const [unansweredCards, setUnansweredCards] = useState([]);

  // State for API loading
  const [isLoadingReport, setIsLoadingReport] = useState(false);
  const [isValidating, setIsValidating] = useState(false);

  // Debounce timer for API calls
  const debounceTimer = useRef(null);

  useAppThemeWhileMounted();
  useLandingFont();
  useReveal(rootRef);

  const toggleCard = (cardIndex) => {
    setOpenCard(openCard === cardIndex ? null : cardIndex);
  };

  // Handle radio button changes
  const handleRadioChange = (category, value) => {
    setCardAnswers((prev) => ({
      ...prev,
      [category]: value,
    }));

    // Remove this card from unanswered list if it was there
    if (unansweredCards.includes(category)) {
      setUnansweredCards((prev) => prev.filter((card) => card !== category));
    }

    // Auto-collapse current card and expand next card
    const cardOrder = [
      "project-type",
      "maturity",
      "sector",
      "function",
      "capability",
      "impact",
      "dummy1",
      "dummy2",
      "dummy3",
    ];
    const currentIndex = cardOrder.indexOf(category);
    const nextIndex = currentIndex + 1;

    // If there's a next card, open it
    if (nextIndex < cardOrder.length) {
      setTimeout(() => {
        setOpenCard(cardOrder[nextIndex]);
      }, 300); // Small delay for smooth transition
    } else {
      // If this is the last card, close it
      setTimeout(() => {
        setOpenCard(null);
      }, 300);
    }
  };

  const handleGenerateRoadmap = () => {
    // Show validation loader
    setIsValidating(true);

    // Simulate validation delay for better UX
    setTimeout(() => {
      // Check if all questions are answered
      const allAnswered = Object.values(cardAnswers).every(
        (answer) => answer !== "",
      );

      if (!allAnswered) {
        // Find which cards are not answered
        const unanswered = Object.keys(cardAnswers).filter(
          (key) => cardAnswers[key] === "",
        );
        setUnansweredCards(unanswered);
        setIsValidating(false);

        alert("Please answer all questions before generating the roadmap.");
        return;
      }

      // Clear any previous validation indicators
      setUnansweredCards([]);

      // Clear company suggestions when opening modal
      setCompanySuggestions([]);
      setShowCompanySuggestions(false);

      // Hide validation loader
      setIsValidating(false);

      // Show modal to collect user details
      setShowModal(true);
    }, 500); // 500ms delay for smooth UX
  };

  // Function to close modal and clear suggestions
  const handleCloseModal = () => {
    setShowModal(false);
    setCompanySuggestions([]);
    setShowCompanySuggestions(false);
    // Clear debounce timer
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }
  };

  // Function to fetch company suggestions from Clearbit API
  const fetchCompanySuggestions = async (query) => {
    // Clear previous timer
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    // If query is empty, clear suggestions
    if (!query || query.trim() === "") {
      setCompanySuggestions([]);
      setShowCompanySuggestions(false);
      return;
    }

    // Debounce the API call
    debounceTimer.current = setTimeout(async () => {
      try {
        setIsLoadingSuggestions(true);
        const response = await fetch(
          `https://autocomplete.clearbit.com/v1/companies/suggest?query=${encodeURIComponent(query)}`,
        );

        if (response.ok) {
          const data = await response.json();
          setCompanySuggestions(data || []);
          setShowCompanySuggestions(true);
        } else {
          setCompanySuggestions([]);
          setShowCompanySuggestions(false);
        }
      } catch (error) {
        console.error("Error fetching company suggestions:", error);
        setCompanySuggestions([]);
        setShowCompanySuggestions(false);
      } finally {
        setIsLoadingSuggestions(false);
      }
    }, 300); // 300ms debounce delay
  };

  const handleUserDetailsChange = (field, value) => {
    setUserDetails((prev) => ({
      ...prev,
      [field]: value,
    }));

    // If company field is being changed, fetch suggestions
    if (field === "company") {
      fetchCompanySuggestions(value);
    }
  };

  // Handle company suggestion selection
  const handleCompanySelect = (company) => {
    // Format the URL - add https:// if domain exists
    const companyUrl = company.domain ? `https://${company.domain}` : "";

    setUserDetails((prev) => ({
      ...prev,
      company: company.name,
      companyUrl: companyUrl,
    }));
    setShowCompanySuggestions(false);
    setCompanySuggestions([]);
  };

  // Handle click outside to close suggestions
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        companyInputRef.current &&
        !companyInputRef.current.contains(event.target) &&
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target)
      ) {
        setShowCompanySuggestions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      // Cleanup debounce timer on unmount
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, []);

  const handleProceedToRoadmap = async () => {
    // Validate user details
    if (!userDetails.name || !userDetails.company || !userDetails.email) {
      alert("Please fill in all fields.");
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(userDetails.email)) {
      alert("Please enter a valid email address.");
      return;
    }

    // Call APIs to save data and get Reimagine Lite Report
    try {
      // Close modal first
      setShowModal(false);

      // Then show loading overlay
      setIsLoadingReport(true);

      // Prepare user details with correct field names for the database API
      const userDetailsForDb = {
        name: userDetails.name,
        email: userDetails.email,
        companyName: userDetails.company,
        companyUrl: userDetails.companyUrl,
      };

      // Call API to save user details to database
      await addReimagineLiteReport(userDetailsForDb);

      // Call API to get Reimagine Lite Report
      const reportData = await getReimagineLiteReport(userDetails, cardAnswers);

      // Navigate to roadmap with API response
      navigate("/reimagine-lite/roadmap", {
        state: {
          selectedFilters: selectedFilters,
          cardAnswers: cardAnswers,
          userDetails: userDetails,
          openCard: openCard,
          reportData: reportData,
        },
      });
    } catch (error) {
      console.error("Error fetching report:", error);
      alert("Failed to generate report. Please try again.");
    } finally {
      setIsLoadingReport(false);
    }
  };

  // Handlers for Connect with our Experts modal
  const handleOpenConnectModal = () => {
    setShowConnectModal(true);
  };

  const handleCloseConnectModal = () => {
    setShowConnectModal(false);
  };

  const handleConnectChange = (field, value) => {
    setConnectForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSendConnectMessage = () => {
    const { firstName, lastName, email, company, message } = connectForm;
    if (!firstName || !lastName || !email || !company || !message) {
      alert("Please fill in all required fields.");
      return;
    }
    // TODO: Integrate with backend/email service
    console.log("Connect form submitted:", connectForm);
    handleCloseConnectModal();
  };

  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="rl rl-lite" id="top" ref={rootRef}>
      <LandingNav variant="lite" />
      <main>
        <LiteHero>
          <Generator
            openCard={openCard}
            cardAnswers={cardAnswers}
            unansweredCards={unansweredCards}
            isValidating={isValidating}
            onToggle={toggleCard}
            onAnswer={handleRadioChange}
            onGenerate={handleGenerateRoadmap}
          />
        </LiteHero>
        <LiteHow />
        <LiteFramework />
        <LiteTrends />
        <LiteBaseline />
        <LiteCta onTalk={handleOpenConnectModal} />
      </main>
      <ScrollToExplore />
      <LandingFooter />

      {showModal && (
        <UserDetailsModal
          userDetails={userDetails}
          companySuggestions={companySuggestions}
          showCompanySuggestions={showCompanySuggestions}
          isLoadingSuggestions={isLoadingSuggestions}
          isLoadingReport={isLoadingReport}
          companyInputRef={companyInputRef}
          suggestionsRef={suggestionsRef}
          onChange={handleUserDetailsChange}
          onCompanyFocus={() => {
            if (companySuggestions.length > 0) {
              setShowCompanySuggestions(true);
            }
          }}
          onCompanySelect={handleCompanySelect}
          onClose={handleCloseModal}
          onProceed={handleProceedToRoadmap}
        />
      )}

      {showConnectModal && (
        <ConnectModal
          form={connectForm}
          onChange={handleConnectChange}
          onClose={handleCloseConnectModal}
          onSend={handleSendConnectMessage}
        />
      )}

      {isLoadingReport && <LoadingOverlay />}
    </div>
  );
}

export default ReImagineLite;
